<template lang="pug">
study-result-student-select-list.study-result-student-select(
  ref='template',
  fullNamePlaceholder='氏名',
  statusPlaceholder='ステータス',
  :userRole='userRole',
  :currentLink='currentLink',
  :sideMenuLinkEvent='sideMenuLinkEvent',
  :logoutEvent='logoutEvent',
  :breadcrumbs='breadcrumbs',
  :breadcrumbMoveEvent='breadcrumbMoveEvent',
  :titleText='titleText',
  :gradeSelectOptions='gradeSelectOptions',
  :searchEvent='searchEvent',
  :downloadEvent='downloadEvent',
  :sort='sort',
  :studentDatas='studentDatas',
  :resultButtonEvent='resultButtonEvent',
  :pagenationStatus='pagenationStatus',
  :pagenationEvent='pagenationEvent',
  @sort-changed='sortChanged'
)
  template(#sideMenuSeparateText) 設定
  template(#logoutText) サインアウト
  template(#searchButtonText) 絞り込む
  template(#downloadIconLabel) ダウンロード
  template(#nickNameHeader) 氏名
  template(#gradeNameHeader) 学年
  template(#statusNameHeader) ステータス
  template(#lastMinuteCheckStatusHeader) 直前チェック実施科目数
  template(#resolveStatusHeader) 解きなおし実施科目数
  template(#resultButtonLabel) 成績表
</template>

<script lang="ts">
import { Component, Mixins, Ref } from 'vue-property-decorator'
import LoginMethods from '@/mixins/LoginMethods'
import GradesApi from '@/mixins/api/GradesApi'
import OriginalCurriculumsApi from '@/mixins/api/OriginalCurriculumsApi'
import StudyContentsApi from '@/mixins/api/StudyContentsApi'
import StudyResultDetailsApi from '@/mixins/api/StudyResultDetailsApi'
import StudyResultStudentSelectList from '@/components/templates/manager/history/StudyResultStudentSelectList.vue'
import { breadcrumbType } from '@/models/breadcrumbModel'
import {
  StudentSelectListType,
  StudentSelectListSortType,
  StudentSelectSearchParamsType,
} from '@/models/studentListModel'
import { InputItem } from '@/models/inputItem'
import { PaginationStatus } from '@/models/paginationModel'
import { LinkSetting, SideMenuSettings } from '@/const/SideMenuSettings'
import { UserRoles } from '@/const/UserRoles'
import { InputItemPlaceholderSetting } from '@/const/DisplaySettings'
import { SortSettings } from '@/const/SortSettings'
import { StudySearchUtils } from '@/utils/StudySearchUtils'
import { StudentManagementUtils } from '@/utils/StudentManagementUtils'
import { StudyManagementUtils } from '@/utils/StudyManagementUtils'

/**
 * 履歴管理・生徒を選ぶ画面（講師・先生）
 */
@Component({
  components: {
    StudyResultStudentSelectList,
  },
})
export default class StudyResultStudentSelect extends Mixins(
  LoginMethods,
  GradesApi,
  OriginalCurriculumsApi,
  StudyContentsApi,
  StudyResultDetailsApi
) {
  // テンプレートへのアクセス
  @Ref() template!: StudyResultStudentSelectList

  // テストID
  private get studyId(): number {
    return Number(this.$route.params.studyId)
  }

  // パンくず
  private breadcrumbs: breadcrumbType[] = [
    {
      label: '履歴管理',
      location: { name: 'BranchHistoryStudyResultManagement' },
    },
    {
      label: '生徒を選ぶ',
    },
  ]

  // 講師・先生権限
  private userRole: UserRoles = UserRoles.BRANCH

  // サイドメニューの現在リンク
  private currentLink: LinkSetting = SideMenuSettings.MANAGER_HISTORY

  // タイトル文字列
  private titleText = ''

  // 学年選択オプション
  private gradeSelectOptions: InputItem[] = []

  // ソート情報
  private sort: StudentSelectListSortType | null = null

  // 生徒情報一覧
  private studentDatas: StudentSelectListType[] = []

  // ページネーションの状態
  private pagenationStatus: PaginationStatus | null = null

  // 現在の検索パラメーター
  private currentSearchParams: StudentSelectSearchParamsType | null = null

  // 現在のページ
  private get currentPage(): number {
    return this.pagenationStatus?.page || 1
  }

  // 教室管理画面Cookie情報
  private get branchCookie() {
    return this.$bcbtCookies.branchCookie.get() || {}
  }

  // 初期表示処理
  private async mounted() {
    this.$loading.start()
    // テスト情報、学年選択オプションを取得
    const apiResponse = await this.getOriginalCurriculumInfo(this.studyId)
    const gradeApiResponse = await this.getGrades()
    this.titleText = StudyManagementUtils.buildStudyContentTitle(apiResponse)
    this.gradeSelectOptions = StudySearchUtils.buildGradeInputItems(
      StudySearchUtils.buildPlaceholder(InputItemPlaceholderSetting.MANAGER_GRADE_ALL),
      gradeApiResponse
    )
    // Cookieに検索条件がセットされている場合は設定して検索
    const studyResultStudentSelectSearch = this.branchCookie.studyResultStudentSelectSearch
    if (studyResultStudentSelectSearch) {
      // 検索条件のソート値からソート設定を取得してセット
      this.sort = {
        nickName: SortSettings.findByCode(studyResultStudentSelectSearch.userNameSort),
        gradeName: SortSettings.findByCode(studyResultStudentSelectSearch.gradeSort),
        statusName: SortSettings.findByCode(studyResultStudentSelectSearch.statusNameSort),
      }
      this.template.gradeValue = studyResultStudentSelectSearch.gradeId
        ? String(studyResultStudentSelectSearch.gradeId)
        : ''
      this.template.fullName = studyResultStudentSelectSearch.nickname
      this.template.status = studyResultStudentSelectSearch.status
      await this.search(studyResultStudentSelectSearch.page)
    } else {
      // それ以外はデフォルトソート値をセットして1ページ目を検索する
      this.setDefaultSort()
      await this.search(1)
    }
    this.$loading.complete()
  }

  // デフォルトソート値をセット
  private setDefaultSort() {
    this.sort = {
      nickName: SortSettings.ASC,
      gradeName: SortSettings.ASC,
      statusName: SortSettings.ASC,
    }
  }

  // 検索ボタン押下イベント
  private async searchEvent() {
    this.$loading.start()
    // ソートを初期化
    this.setDefaultSort()
    // 1ページ目を検索
    await this.search(1)
    this.$loading.complete()
  }

  // ページ遷移イベント
  private async pagenationEvent(page: number) {
    this.$loading.start()
    // 指定されたページを検索
    await this.search(page)
    this.$loading.complete()
  }

  // ソート変更時イベント
  private async sortChanged(sort: StudentSelectListSortType) {
    this.$loading.start()
    // ソートを更新
    this.sort = sort
    // 現在のページを検索
    await this.search(this.currentPage)
    this.$loading.complete()
  }

  // 検索処理
  private async search(page: number) {
    if (!this.sort) {
      return
    }
    // 受験済み生徒一覧検索API呼び出し
    this.currentSearchParams = {
      page: page,
      gradeId: this.template.gradeValue ? Number(this.template.gradeValue) : null,
      nickname: this.template.fullName,
      status: this.template.status,
      originalCurriculumId: this.studyId,
      userNameSort: this.sort.nickName.code,
      gradeSort: this.sort.gradeName.code,
      statusNameSort: this.sort.statusName.code,
    }
    const studentApiResponse = await this.searchStudentSubjectHistories(this.currentSearchParams, this.userRole)
    this.studentDatas = StudentManagementUtils.formatStudentSelectDatas(studentApiResponse)
    this.pagenationStatus = StudentManagementUtils.getStudentSelectPagenationStatus(studentApiResponse)
    // Cookieに検索条件をセット
    const branchCookie = this.branchCookie
    branchCookie.studyResultStudentSelectSearch = this.currentSearchParams
    await this.$bcbtCookies.branchCookie.set(branchCookie)
  }

  // サイドメニュー遷移イベント
  private sideMenuLinkEvent(link: LinkSetting) {
    // 設定されたリンク先ページへ遷移
    this.$router.push({ name: `Branch${link.name}` })
  }

  // ログアウトイベント
  private logoutEvent() {
    this.logout()
    // ログインページへ遷移
    this.$router.push({ name: 'BranchLogin' })
  }

  // パンくず遷移イベント
  private breadcrumbMoveEvent(breadcrumb: breadcrumbType) {
    if (breadcrumb.location) {
      this.$router.push(breadcrumb.location)
    }
  }

  // 成績表ボタン押下時のイベント
  private resultButtonEvent(studentId: number) {
    this.$router.push({
      name: 'BranchHistoryStudyResultSummary',
      params: {
        studyId: String(this.studyId),
        studentId: String(studentId),
      },
    })
  }

  // ダウンロードイベント
  private async downloadEvent() {
    // Cookieから前画面の実施年度選択情報を取得しリクエストパラメータに使用
    if (!this.branchCookie.studyResultManagementSearch) return

    // ダウンロード処理
    this.$loading.start()
    try {
      // pdfデータを取得
      const [data] = await this.downloadStudyResultPdfStudents(
        Number(this.branchCookie.studyResultManagementSearch.years),
        this.studyId,
        this.userRole
      )
      // 別タブでpdfを開く
      window.open(URL.createObjectURL(data), '_blank')
      this.$loading.complete()
    } catch {
      alert('ファイルが存在しません。')
    }
  }
}
</script>
